import { decodeJwt } from '@/auth/infrastructure/utils/authUtils';

const POLICY_CLAIM_KEYS = ['PermissionPolicies', 'permissionPolicies', 'permission_policies'] as const;

export interface ParsedJwtPermissionPolicies {
  policies: Set<string>;
  /** When false, the JWT did not include any known policy claim — callers may treat all policies as granted for backward compatibility. */
  hasExplicitPolicies: boolean;
}

function addPolicyValues(target: Set<string>, value: unknown): void {
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (typeof entry === 'string' && entry.trim() !== '') {
        target.add(entry.trim());
      }
    }
    return;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      addPolicyValues(target, parsed);
      return;
    }
  } catch {
    // Fall through to comma-separated parsing.
  }

  for (const part of value.split(',')) {
    const trimmed = part.trim();
    if (trimmed !== '') {
      target.add(trimmed);
    }
  }
}

/**
 * Reads UM permission policy names from the JWT payload.
 * Supports array claims, JSON-encoded arrays, and comma-separated strings.
 */
export function parseJwtPermissionPolicies(token: string | undefined): ParsedJwtPermissionPolicies {
  const policies = new Set<string>();
  if (!token) {
    return { policies, hasExplicitPolicies: false };
  }

  const claims = decodeJwt<Record<string, unknown>>(token);
  if (!claims) {
    return { policies, hasExplicitPolicies: false };
  }

  for (const key of POLICY_CLAIM_KEYS) {
    if (!(key in claims)) continue;
    addPolicyValues(policies, claims[key]);
    return { policies, hasExplicitPolicies: true };
  }

  return { policies, hasExplicitPolicies: false };
}
